import { useContext, useCallback } from 'react';
import { ProCalculatorContext } from '../contexts/ProCalculatorContext';

// Hook for Pro Calculator functionality

export const useProCalculator = () => {
  const context = useContext(ProCalculatorContext);
  
  if (!context) {
    throw new Error('useProCalculator must be used within a ProCalculatorProvider');
  }
  
  // Function to update a dimensions row
  const updateDimensionsRow = useCallback((id, field, value) => {
    // Call the update function from the context directly
    // This function within the context now handles recalculations and state updates
    if (context.updateDimensionsRow) {
       context.updateDimensionsRow(id, field, value);
    } else {
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- 3.4.2026
  }, [context.updateDimensionsRow]); // Depend on the context's update function

  // Function to update a color row
  const updateColorRow = (id, field, value) => {
    context.setColorRows(prev => 
      prev.map(row => 
        row.id === id ? { ...row, [field]: value } : row
      )
    );
  };
  
  // Function to update a color percentage
  const updateColorPercentage = (rowId, colorIndex, value) => {
    const { colorNames, setColorRows } = context;
    const numColors = colorNames.length;
    const parsed = parseFloat(value);
    if (value === '' || value === null || value === undefined || isNaN(parsed)) return;

    setColorRows(prevColorRows => 
      prevColorRows.map(row => {
        if (row.cookieId === rowId) {
          const newValue = Math.max(0, Math.min(100, parsed));
          let newPercentages = [...row.percentages];

          if (numColors === 2) {
            // Auto-balance for exactly two colors
            const otherColorIndex = colorIndex === 0 ? 1 : 0;
            const otherValue = Math.max(0, 100 - newValue);
            newPercentages = [];
            newPercentages[colorIndex] = newValue;
            newPercentages[otherColorIndex] = otherValue;
          } else {
            // If 100% set on one color, zero all others
            if (newValue === 100) {
              newPercentages = newPercentages.map(() => 0);
            }
            newPercentages[colorIndex] = newValue;
            while (newPercentages.length < numColors) {
              newPercentages.push(0);
            }
            if (newPercentages.length > numColors) {
              newPercentages = newPercentages.slice(0, numColors);
            }
          }
          
          return { ...row, percentages: newPercentages };
        }
        return row;
      })
    );
  };

  // Function to add a new color
  const addColor = () => {
    // eslint-disable-next-line no-unused-vars -- 3.4.2026
    const { setColorNames, colorRows, setColorRows } = context;
    let newColorNames = [];

    // Add a new color name
    setColorNames(prev => {
      newColorNames = [...prev, `Color ${prev.length + 1}`];
      return newColorNames;
    });
    
    // Update all color rows to add a percentage for the new color
    setColorRows(prevColorRows => {
      const numColors = newColorNames.length; // Get the NEW total number of colors

      return prevColorRows.map(row => {
        let newPercentages = [...row.percentages];
        
        if (numColors === 1) {
          // First color added, set to 100%
          newPercentages = [100];
        } else if (numColors === 2) {
          // Second color added — no longer an obvious default, so reset both to 0%
          // and let the baker decide the split.
          newPercentages = [0, 0];
        } else {
          // Third or subsequent color added, initialize to 0%
          newPercentages.push(0); // Add 0 for the new color
        }
        
        return { ...row, percentages: newPercentages };
      });
    });
  };

  // Function to remove a color
  const removeColor = (colorIndex) => {
    // eslint-disable-next-line no-unused-vars -- 3.4.2026
    const { colorNames, setColorNames, colorRows, setColorRows } = context;

    // Remove the color name
    const newColorNames = colorNames.filter((_, index) => index !== colorIndex);
    setColorNames(newColorNames);
    
    // Remove the corresponding percentage from each color row
    setColorRows(prevColorRows => {
      const updatedRows = prevColorRows.map(row => {
        const newPercentages = row.percentages.filter((_, index) => index !== colorIndex);
        return { ...row, percentages: newPercentages };
      });

      // If only one color remains, set its percentage to 100 for all rows
      if (newColorNames.length === 1) {
        return updatedRows.map(row => ({
          ...row,
          percentages: [100] // The only remaining color gets 100%
        }));
      }

      return updatedRows; // Return rows with percentage removed
    });
  };
  
  // Function to update an order row
  const updateOrderRow = (cookieId, count) => {
    context.setOrderRows(prev => {
      // Check if an order row already exists for this cookie
      const existingRow = prev.find(row => row.cookieId === cookieId);
      
      if (existingRow) {
        // Update the existing row
        return prev.map(row => 
          row.cookieId === cookieId ? { ...row, count } : row
        );
      } else {
        // Create a new row
        const newId = prev.length > 0 ? Math.max(...prev.map(row => row.id)) + 1 : 1;
        return [...prev, { id: newId, cookieId, count }];
      }
    });
  };
  
  // Function to calculate color icing amounts for a cookie
  const calculateColorIcing = (cookieId, count = 1) => {

    // Find the dimensions row for this cookie
    const dimensionsRow = context.dimensionsRows.find(row => row.cookieId === cookieId);
    
    // Find the color row for this cookie
    const colorRow = context.colorRows.find(row => row.cookieId === cookieId);
    
    // If either is missing, return zeros
    if (!dimensionsRow || !colorRow) {
      return Array(context.colorNames.length).fill(0);
    }
    
    // Get the total icing needed for this cookie
    const totalIcingNeeded = parseFloat(dimensionsRow.icingNeeded) || 0;
    
    // Calculate the amount for each color based on percentages
    const calculatedAmounts = colorRow.percentages.map(percentage => {
      const amount = totalIcingNeeded * (percentage / 100) * count;
      return Math.round(amount * 100) / 100;
    });
    return calculatedAmounts;
  };
  
  
  // Function to normalize percentages in each row to 100%
  const normalizeColorPercentages = () => {
    // eslint-disable-next-line no-unused-vars -- 3.4.2026
    const { colorRows, setColorRows } = context;

    setColorRows(prevColorRows => 
      prevColorRows.map(row => {
        const currentPercentages = row.percentages;
        const sum = currentPercentages.reduce((acc, val) => acc + val, 0);

        // Only normalize if sum is not 0 and not already 100
        if (sum <= 0 || Math.abs(sum - 100) < 0.01) { // Allow for small floating point errors
          return row; // No changes needed
        }

        const scaleFactor = 100 / sum;
        let scaledPercentages = currentPercentages.map(p => p * scaleFactor);

        // Adjust the last percentage slightly to ensure the sum is exactly 100
        // due to potential floating point inaccuracies after scaling.
        const scaledSum = scaledPercentages.reduce((acc, val) => acc + val, 0);
        const difference = 100 - scaledSum;
        
        // Find the largest percentage index to apply the difference for stability
        let largestIndex = 0;
        for (let i = 1; i < scaledPercentages.length; i++) {
          if (scaledPercentages[i] > scaledPercentages[largestIndex]) {
            largestIndex = i;
          }
        }
        scaledPercentages[largestIndex] += difference;

        // Ensure no percentage is negative after adjustment (rare edge case)
        scaledPercentages = scaledPercentages.map(p => Math.max(0, p));
        
        // Re-normalize if adjustment made it go over 100 (due to negative edge case)
        const finalSum = scaledPercentages.reduce((acc, val) => acc + val, 0);
        if(Math.abs(finalSum - 100) > 0.01 && finalSum > 0) {
            const finalScale = 100 / finalSum;
            scaledPercentages = scaledPercentages.map(p => p * finalScale);
             // Final rounding adjustment if needed (usually very minor)
            const finalSum2 = scaledPercentages.reduce((acc, val) => acc + val, 0);
            scaledPercentages[largestIndex] += (100 - finalSum2);
        }

        // Round percentages for display (optional, but cleaner)
        // scaledPercentages = scaledPercentages.map(p => Math.round(p));

        // Round to 2 decimal places
        let rounded = scaledPercentages.map(p => parseFloat(p.toFixed(2)));
        // Fix rounding remainder (e.g. 33.33+33.33+33.33=99.99) by adding it to the first non-zero entry
        const roundedSum = rounded.reduce((acc, p) => acc + p, 0);
        const remainder = parseFloat((100 - roundedSum).toFixed(2));
        if (remainder !== 0) {
          const firstNonZero = rounded.findIndex(p => p > 0);
          if (firstNonZero !== -1) rounded[firstNonZero] = parseFloat((rounded[firstNonZero] + remainder).toFixed(2));
        }
        return { ...row, percentages: rounded };
      })
    );
  };

  // Function to sort dimensions rows alphabetically by cookie name
  const sortDimensionsRows = () => {
    context.setDimensionsRows(prev => {
      // Create a map of cookieId to cookie name
      const cookieNameMap = {};
      context.cookieRows.forEach(row => {
        cookieNameMap[row.cookieId] = row.cookieName;
      });
      
      // Sort the dimensions rows by cookie name
      return [...prev].sort((a, b) => {
        const nameA = cookieNameMap[a.cookieId] || '';
        const nameB = cookieNameMap[b.cookieId] || '';
        return nameA.localeCompare(nameB);
      });
    });
  };
  
  // Function to get color style for display
  const getColorStyle = (colorName) => {
    // Map color names to CSS colors
    const colorMap = {
      'White': '#ffffff',
      'Black': '#000000',
      'Red': '#ff0000',
      'Blue': '#0000ff',
      'Green': '#00ff00',
      'Yellow': '#ffff00',
      'Orange': '#ffa500',
      'Purple': '#800080',
      'Pink': '#ffc0cb',
      'Brown': '#a52a2a',
      'Gray': '#808080',
      'Teal': '#008080',
      'Navy': '#000080',
      'Maroon': '#800000',
      'Olive': '#808000',
      'Lime': '#00ff00',
      'Aqua': '#00ffff',
      'Fuchsia': '#ff00ff',
      'Silver': '#c0c0c0',
      'Gold': '#ffd700'
    };
    
    // Exact match
    if (colorMap[colorName]) {
      return { backgroundColor: colorMap[colorName] };
    }
    // Fuzzy match: find any known color keyword in the name
    const lowerName = colorName.toLowerCase();
    const fuzzyKey = Object.keys(colorMap).find(k => lowerName.includes(k.toLowerCase()));
    return { backgroundColor: fuzzyKey ? colorMap[fuzzyKey] : '#cccccc' };
  };
  
  // Function to handle image processing results
  const handleImageProcessed = (width, length, area) => {
    if (context.currentAnalyzingRow) {
      // Update the dimensions row with the processed image data
      updateDimensionsRow(context.currentAnalyzingRow.id, 'width', width.toFixed(2));
      updateDimensionsRow(context.currentAnalyzingRow.id, 'length', length.toFixed(2));
      
      // Close the image analyzer
      context.setShowImageAnalyzer(false);
      context.setCurrentAnalyzingRow(null);
    }
  };
  
  // Return the original context, potentially extended with NEW, NON-OVERRIDING utilities
  return {
    ...context,
    updateDimensionsRow,
    updateColorRow,
    updateColorPercentage,
    addColor,
    removeColor,
    updateOrderRow,
    calculateColorIcing,
    sortDimensionsRows,
    getColorStyle,
    handleImageProcessed,
    normalizeColorPercentages // Export the new function
  };
};
